import { useMemo, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { startMasterDataFetching } from "../redux/reducers/drmLiveLoadMasterDataSlice";
import { primitiveToString, toSafeString } from "../helpers";

const selectMasterDataByKey = (key) => (state) => {
  const data = state.masterData?.masterData?.[key];
  if (!data) {
    return {
      data: [],
      isLoading: false,
      onPage: 1,
      pageSize: 50,
      totalPages: null,
      totalRecords: null
    };
  }
  return {
    data: data.data || [],
    isLoading: data.isLoading || false,
    onPage: data.onPage || 1,
    pageSize: data.pageSize || 50,
    totalPages: data.totalPages || null,
    totalRecords: data.totalRecords || null
  };
};

export const useLiveLoadAutocomplete = (key, getLabel) => {
  const dispatch = useDispatch();
  
  const selector = useMemo(() => selectMasterDataByKey(key), [key]);
  const options = useSelector(selector, shallowEqual);

  const handleLoadPage = useCallback(async ({ searchTerms = [], onPage = 1 }) => {
    return dispatch(startMasterDataFetching({ 
      body: searchTerms, 
      masterDataType: key, 
      onPage,
    }));
  }, [key]);

  const handleSearchOptions = useCallback(async (searchTerms) => {
    return dispatch(
      startMasterDataFetching({ 
        body: searchTerms, 
        masterDataType: key 
      })
    );
  }, [key]);

  const handleResetFilter = useCallback(async () => {
    if(key === "tariffIds" || key === "tariffEquipmentMapping") return;
    return dispatch(
      startMasterDataFetching({ 
        body: [], 
        masterDataType: key 
      })
    );
  }, [key]);

  const stableGetLabel = useCallback((option) => {
    if (option === null || option === undefined) {
      return "";
    }

    const primitive = primitiveToString(option);
    if (primitive !== null) {
      return primitive;
    }

    if (typeof getLabel === "function") {
      const derived = getLabel(option);
      if (derived !== undefined && derived !== null && derived !== "") {
        return toSafeString(derived);
      }
    }

    if (typeof option === "object") {
      const fallback = option.label ?? option.name ?? option.value ?? option.id;
      return toSafeString(fallback);
    }

    return toSafeString(option);
  }, [getLabel]);

  return {
    options,
    handleLoadPage,
    handleSearchOptions,
    handleResetFilter,
    stableGetLabel
  };
};